import styled, { css } from 'styled-components';
import { down } from 'styled-breakpoints';

import { TransientProps } from '@common/types';

type withActive = TransientProps<{ active: boolean }>;
type withLoading = TransientProps<{ loading: boolean }>;
type RoomContainerProps = withLoading &
  withActive & {
    $deleting: boolean;
    $moderator?: boolean;
  };

export const Container = styled.article<RoomContainerProps>`
  ${({ theme, $active: isActive, $deleting: isDeleting }) => css`
    width: 100%;
    border-radius: 1.25rem;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.base.border};
    box-shadow: ${theme.shadows.sm};
    overflow: hidden;
    transition: ${theme.transition.default};

    :hover {
      box-shadow: ${theme.shadows.md};
    }

    ${!isActive &&
    css`
      pointer-events: none;
    `}

    ${isDeleting &&
    css`
      opacity: 0.4;
    `}

    ${down('md')} {
      max-width: 100%;
    }
  `}
`;

export const PostHeader = styled.header`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem 0;

    > div:first-child {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    h3 {
      font-size: 0.9375rem;
      font-weight: 700;
      color: ${theme.colors.foreground[100]};
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    time {
      font-size: 0.8125rem;
      color: ${theme.colors.foreground[40]};
    }
  `};
`;

export const ModeratorBadge = styled.span`
  ${() => css`
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    background: #fff4d9;
    color: #e8a500;
  `}
`;

export const ModeratorOptionsContainer = styled.div`
  ${({ theme }) => css`
    > button,
    > div > button {
      width: 2.25rem;
      height: 2.25rem;
      border-radius: 0.75rem;
      background: transparent;
      color: ${theme.colors.foreground[40]};
      transition: ${theme.transition.default};

      :hover {
        background: ${theme.colors.background[80]};
        color: ${theme.colors.foreground[80]};
      }
    }

    .tippy-content {
      padding: 0;
    }
  `}
`;

export const ModeratorOptionsButton = styled.button`
  ${({ theme }) => css`
    background: none;
    white-space: nowrap;
    width: 100%;
    border-radius: 0;
    justify-content: flex-start;
    transition: 180ms ease-in-out;

    :hover {
      background: ${theme.colors.brand.primary.default};
      color: ${theme.colors.base.text};
    }

    :first-child {
      border-radius: 0.25rem 0.25rem 0 0;
    }
    :last-child {
      border-radius: 0 0 0.25rem 0.25rem;
    }
  `}
`;

export const PostMain = styled.main`
  ${({ theme }) => css`
    padding: 0.875rem 1.5rem 1rem;

    p {
      font-size: 0.9375rem;
      line-height: 1.7;
      color: ${theme.colors.foreground[80]};
      word-break: break-word;
      margin: 0;

      & + p {
        margin-top: 0.5rem;
      }
    }
  `};
`;

export const PostImageWrapper = styled.div`
  ${() => css`
    margin: 0 1.5rem 1rem;
    border-radius: 1rem;
    overflow: hidden;

    > img {
      display: block;
      width: 100%;
      max-height: 280px;
      object-fit: cover;
    }
  `};
`;

export const PostStats = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0 1.5rem 0.875rem;
    font-size: 0.8125rem;
    color: ${theme.colors.foreground[40]};
  `}
`;

export const PostStat = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const InlineButton = styled.button`
  ${() => css`
    background: none;
    border: 0;
    padding: 0;
    font-size: 0.8125rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;

    :hover {
      text-decoration: underline;
    }
  `};
`;

export const PostFooter = styled.footer``;

type PostFooterActionsProps = TransientProps<{
  commentsVisible: boolean;
  voted: boolean;
}>;

export const PostFooterActions = styled.section<PostFooterActionsProps>`
  ${({ theme, $commentsVisible: areCommentsVisible, $voted: hasVoted }) => css`
    display: flex;
    border-top: 1px solid ${theme.colors.base.border};
    padding: 0.25rem 0.75rem;

    ${areCommentsVisible &&
    css`
      border-bottom: 1px solid ${theme.colors.base.border};
    `}

    > button {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.75rem 0;
      border: none;
      background: transparent;
      cursor: pointer;
      color: ${theme.colors.foreground[40]};
      font-size: 0.875rem;
      font-weight: 500;
      border-radius: 0.75rem;
      transition: ${theme.transition.default};

      :not(:disabled):hover {
        background: ${theme.colors.background[80]};
        color: ${theme.colors.brand.primary.default};
      }

      ${hasVoted &&
      css`
        :first-child {
          color: #ef4444;

          svg {
            fill: #ef4444;
            stroke: #ef4444;
          }
        }
      `}
    }
  `};
`;
